<script setup lang="ts">
/**
 * Anzeige der Kurzmeldungen. Liegt einmal in `App.vue` und wird von überall über
 * den Store gefuettert.
 *
 * `aria-live="polite"` sorgt dafür, dass Screenreader die Meldung vorlesen, ohne
 * die gerade laufende Ausgabe zu unterbrechen.
 */
import Icon from './Icon.vue';
import { useToastStore } from '../stores/toasts';

const toasts = useToastStore();
</script>

<template>
  <div class="toast-host" aria-live="polite" aria-atomic="false">
    <TransitionGroup name="toast">
      <div
        v-for="toast in toasts.toasts"
        :key="toast.id"
        class="toast"
        :class="`toast--${toast.kind}`"
      >
        <Icon :name="toast.kind === 'success' ? 'bestaetigen' : 'warnung'" :size="16" />
        <span>{{ toast.text }}</span>
        <button
          type="button"
          class="toast__close"
          aria-label="Meldung schließen"
          @click="toasts.entfernen(toast.id)"
        >
          <Icon name="schliessen" :size="14" />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-host {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 26rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
  font-size: 0.875rem;
  pointer-events: auto;
}

.toast--success {
  border-color: var(--tint-green-strong);
  color: var(--tint-green-text);
  background: var(--color-success-soft);
}

.toast--error {
  border-color: var(--tint-red-strong);
  color: var(--tint-red-text);
  background: var(--color-error-soft);
}

.toast__close {
  display: inline-flex;
  margin-left: auto;
  padding: 0.15rem;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  opacity: 0.7;
}
.toast__close:hover {
  opacity: 1;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity var(--t-base),
    transform var(--t-base);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(0.5rem);
}
</style>
